/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Tag, Spin } from '@arco-design/web-react';
import { RootState } from '@/store';
import Config from '@/config';
import styles from './index.module.less';
import PlayIcon from '@/assets/icon/play.svg';
import PauseIcon from '@/assets/icon/pause.svg';
import CopyIcon from '@/assets/icon/copy.svg';
import ReplayIcon from '@/assets/icon/replay.svg';

import { useAppContent } from '@/context/AppContent';

const lines: (string | React.ReactNode)[] = [];

function Conversation(props: React.HTMLAttributes<HTMLDivElement>) {
  const { className, ...rest } = props;
  const msgHistory = useSelector((state: RootState) => state.room.msgHistory);
  const { userId } = useSelector((state: RootState) => state.room.localUser);
  const isAIReady = msgHistory.length > 0;
  const containerRef = useRef<HTMLDivElement>(null);

  const userInfoString = localStorage.getItem('userInfo') || '{}';
  const userInfo = JSON.parse(userInfoString);

  const { sendText2Audio, inPlaying, interrupt, sendReplayMessage } = useAppContent();

  const [currentPlayRecord, setCurrentPlayRecord] = useState<Number>();
  const [bubbleOpacities, setBubbleOpacities] = useState<{ [key: number]: number }>({});
  const bubbleRefs = useRef<Array<HTMLDivElement | null>>([]);

  const handleClickPlayAudio = (message: string, index: number) => {
    interrupt();
    if (!inPlaying) {
      setCurrentPlayRecord(index);
      sendText2Audio(message);
    }
  };

  useEffect(() => {
    if (!inPlaying) {
      setCurrentPlayRecord(0);
    }
  }, [inPlaying]);

  function estimateSpeechDuration(
    text: string,
    options = {
      wordsPerMinute: 150, // 平均语速（英语通常150词/分钟）
      charPerMinute: 480, // 中文字符参考速度（480字/分钟）
      pausePerSentence: 0.3, // 每句停顿时间（秒）
      mode: 'auto', // 检测模式：'auto'|'words'|'chars'
    }
  ) {
    // 清理文本并初始化参数
    // @ts-ignore
    const cleaned = text
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/[。，、；：！？]/g, (match: any) => `${match} `); // 为中文标点添加空格
    // 自动检测模式（根据是否存在中文）
    const detectMode = options.mode === 'auto' ? (/[\u4e00-\u9fa5]/.test(cleaned) ? 'chars' : 'words') : options.mode;
    let baseTime;

    if (detectMode === 'words') {
      // 英文单词模式
      const wordCount = (cleaned.match(/[\wÀ-ÿ]+/g) || []).length;
      baseTime = (wordCount / options.wordsPerMinute) * 60;
    } else {
      // 中文字符模式
      const charCount = cleaned.replace(/\s/g, '').length;
      baseTime = (charCount / options.charPerMinute) * 60;
    }
    // 计算句子停顿（支持中英文标点）
    const sentenceCount = (cleaned.match(/[.!?。！？]+/g) || []).length;
    const pauseTime = sentenceCount * options.pausePerSentence;
    return Math.ceil(baseTime + pauseTime);
  }

  const handleCopy = (value: string) => {
    if (navigator.clipboard) {
      // 使用现代 API
      navigator.clipboard.writeText(value);
    } else {
      // 使用备选方案
      // 创建一个临时的 textarea 元素
      const textarea = document.createElement('textarea');
      textarea.value = value;
      document.body.appendChild(textarea);
      // 选中文本
      textarea.select();
      textarea.setSelectionRange(0, value.length); // 兼容移动设备
      // 复制文本
      try {
        document.execCommand('copy');
      } catch (err) {
      } finally {
        // 移除临时的 textarea 元素
        document.body.removeChild(textarea);
      }
    }
  };

  const handleReplay = (value: string, recordId: string = '') => {
    sendReplayMessage(value, recordId);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight - container.clientHeight;
    }
  }, [msgHistory.length]);

  const updateBubbleOpacities = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    // 只有内容高度大于容器高度（可滚动）且滚动距离超过50px时才处理渐变透明
    if (container.scrollHeight <= container.clientHeight || container.scrollTop < 50) {
      setBubbleOpacities({});
      return;
    }

    // 渐变区：容器顶部 ~ 容器中线
    const containerRect = container.getBoundingClientRect();
    const fadeStart = containerRect.top;
    const fadeEnd = containerRect.top + containerRect.height / 2;

    const newOpacities: { [key: number]: number } = {};
    bubbleRefs.current.forEach((bubble, idx) => {
      if (!bubble) return;
      const rect = bubble.getBoundingClientRect();
      const bubbleBottom = rect.bottom; // 用底边判定

      if (bubbleBottom < fadeEnd) {
        // 在渐变区，底边越靠近顶部越透明，越靠近中线越不透明
        const percent = (bubbleBottom - fadeStart) / (fadeEnd - fadeStart);
        const opacity = Math.max(0.3, Math.min(1, percent));
        newOpacities[idx] = opacity;
      } else {
        // 渐变区下方，完全不透明
        newOpacities[idx] = 1;
      }
    });
    setBubbleOpacities(newOpacities);
  }, [msgHistory.length]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    updateBubbleOpacities();
    container.addEventListener('scroll', updateBubbleOpacities);
    window.addEventListener('resize', updateBubbleOpacities);
    return () => {
      container.removeEventListener('scroll', updateBubbleOpacities);
      window.removeEventListener('resize', updateBubbleOpacities);
    };
  }, [msgHistory.length, updateBubbleOpacities]);

  useEffect(() => {
    setTimeout(updateBubbleOpacities, 0);
  }, [msgHistory.length, updateBubbleOpacities]);

  return (
    <div ref={containerRef} className={`${styles.conversation} ${className}`} {...rest}>
      {lines.map((line) => line)}
      {!isAIReady ? (
        <div className={styles.aiReadying}>
          <Spin size={16} className={styles['aiReading-spin']} />
          初始化中, 请稍侯
        </div>
      ) : null}
      <div className={styles.messageList}>
        {msgHistory?.map(({ value, user, recordId }, index) => {
          // @ts-ignore
          const isUserMsg = user == userInfo.id || user == userId;
          const isRobotMsg = user == Config.BotName;
          if (!isUserMsg && !isRobotMsg) {
            return '';
          }
          // @ts-ignore
          return isUserMsg ? (
            <div
              className={`${styles.sentence} ${styles.user}`}
              key={`msg-${index}`}
              ref={(el) => (bubbleRefs.current[index] = el)}
              style={{
                opacity: bubbleOpacities[index] ?? 1,
                transition: 'opacity 0.3s',
              }}
            >
              <div className={styles.content}>{value}</div>
            </div>
          ) : (
            <div
              className={styles.messageItem}
              key={`msg-${index}`}
              ref={(el) => (bubbleRefs.current[index] = el)}
              style={{
                opacity: bubbleOpacities[index] ?? 1,
                transition: 'opacity 0.3s',
              }}
            >
              <div className={styles.sentenceWrapper}>
                <div className={styles.audioContainer}>
                  <div className={styles.audioBoxPlace}></div>
                  <div className={styles.audioButton} onClick={() => handleClickPlayAudio(value, index)}>
                    <img src={currentPlayRecord == index && inPlaying ? PauseIcon : PlayIcon} alt="" style={{ width: '9px', height: '9px' }} />
                    {/*@ts-ignore*/}
                    <span>{estimateSpeechDuration(value)}s"</span>
                  </div>
                </div>
                <div className={`${styles.sentence} ${styles.robot}`}>
                  <div className={styles.content}>{value}</div>
                </div>
              </div>
              <div className={`${styles.sentenceToolbarBox}`}>
                <img src={CopyIcon} alt="" style={{ width: '20px', height: '20px' }} onClick={() => handleCopy(value)} />
                <img src={ReplayIcon} alt="" style={{ width: '20px', height: '20px' }} onClick={() => handleReplay(value, recordId)} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Conversation;
